import { Todo, Project } from '../lib/supabase'

export interface ShareContent {
  text: string
  hashtags: string[]
  url?: string
}

/**
 * タスク完了をXでシェア
 */
export const shareTaskCompletionToTwitter = (todo: Todo) => {
  const text = `✅ タスク完了！\n「${todo.title}」\n\n#TaStacK #タスク管理 #生産性向上`
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
  window.open(url, '_blank', 'width=550,height=420')
}

/**
 * プロジェクト完了をXでシェア
 */
export const shareProjectCompletionToTwitter = (project: Project, stats: {
  totalTasks: number
  completedTasks: number
  duration: number
}) => {
  const text = `🎉 プロジェクト完了！\n\n「${project.name}」\n完了タスク: ${stats.completedTasks}個\n期間: ${stats.duration}日\n\n#TaStacK #プロジェクト管理 #達成`
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
  window.open(url, '_blank', 'width=550,height=420')
}

/**
 * 週次レポートをXでシェア
 */
export const shareWeeklyReportToTwitter = (stats: {
  completedTasks: number
  inProgressTasks: number
  completionRate: number
  projectName?: string
}) => {
  const text = `📊 今週の進捗レポート${stats.projectName ? `（${stats.projectName}）` : ''}\n\n✅ 完了: ${stats.completedTasks}個\n🔄 進行中: ${stats.inProgressTasks}個\n📈 達成率: ${stats.completionRate}%\n\n#TaStacK #週次レポート #生産性`
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
  window.open(url, '_blank', 'width=550,height=420')
}

/**
 * Facebookでシェア
 */
export const shareToFacebook = (text: string) => {
  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://app.tastack.net')}&quote=${encodeURIComponent(text)}`
  window.open(url, '_blank', 'width=550,height=420')
}

/**
 * LinkedInでシェア
 */
export const shareToLinkedIn = (_text: string) => {
  const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://app.tastack.net')}`
  window.open(url, '_blank', 'width=550,height=520')
}

/**
 * Web Share API（モバイル対応）
 */
export const shareViaWebShareAPI = async (content: ShareContent) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'TaStacK - タスク管理',
        text: content.text,
        url: content.url || 'https://app.tastack.net'
      })
      return true
    } catch (error) {
      console.error('Share failed:', error)
      return false
    }
  }
  return false
}

/**
 * クリップボードにコピー
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Copy failed:', error)
    return false
  }
}

/**
 * Xスレッド用テキスト生成
 */
export const generateTwitterThread = (project: Project, tasks: Todo[]): string[] => {
  const completedTasks = tasks.filter(t => t.status === 'completed')
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress')

  const thread = [
    `🚀 プロジェクト「${project.name}」の進捗報告\n\n1/${Math.ceil(tasks.length / 3) + 3}`,
    `✅ 完了したタスク（${completedTasks.length}個）\n\n${completedTasks.slice(0, 3).map(t => `・${t.title}`).join('\n')}\n\n2/${Math.ceil(tasks.length / 3) + 3}`,
    `🔄 進行中のタスク（${inProgressTasks.length}個）\n\n${inProgressTasks.slice(0, 3).map(t => `・${t.title}`).join('\n')}\n\n3/${Math.ceil(tasks.length / 3) + 3}`,
  ]

  if (completedTasks.length > 0) {
    const completionRate = Math.round((completedTasks.length / tasks.length) * 100)
    thread.push(`📊 統計データ\n\n完了率: ${completionRate}%\nタスク総数: ${tasks.length}個\n\n#TaStacK でプロジェクト管理中\n\n${thread.length + 1}/${Math.ceil(tasks.length / 3) + 3}`)
  }

  return thread
}

/**
 * ハッシュタグ生成
 */
export const generateHashtags = (context: 'task' | 'project' | 'team'): string[] => {
  const baseHashtags = ['TaStacK', 'タスク管理', '生産性']

  switch (context) {
    case 'task':
      return [...baseHashtags, '目標達成', '習慣化']
    case 'project':
      return [...baseHashtags, 'プロジェクト管理', 'チームワーク']
    case 'team':
      return [...baseHashtags, 'チームコラボ', 'リモートワーク']
    default:
      return baseHashtags
  }
}
